import type { AutoScrollOptions, DragEndEvent, Modifier, UniqueIdentifier } from '@dnd-kit/core';
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import type { ReactNode } from 'react';
import React from 'react';
import { DragHandle, SortableItem } from './components';

const autoScroll: AutoScrollOptions = { threshold: { x: 0, y: 0.2 }, acceleration: 1 };

interface BaseItem {
	id: UniqueIdentifier;
}

interface Props<T extends BaseItem> {
	containerClassName: string;
	items: T[];
	modifiers?: Modifier[];
	onChange({ oldIndex, newIndex }): void;
	renderItem(item: T, index?: number): ReactNode;
}

export function SortableList<T extends BaseItem>({ modifiers, containerClassName, items, onChange, renderItem }: Props<T>) {
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const onDragEnd = ({ active, over }: DragEndEvent) => {
		if (active.id !== over?.id) {
			const newIndex: number = over?.id === null ? 0 : Number(over.data.current.sortable.index);
			onChange({ oldIndex: active.data.current.sortable.index, newIndex });
		}
	};

	return (
		<DndContext sensors={sensors} onDragEnd={onDragEnd} modifiers={modifiers} autoScroll={autoScroll}>
			<SortableContext items={items} strategy={rectSortingStrategy}>
				<div className={containerClassName}>
					{items.map((item, index) => {
						return <React.Fragment key={item.id}>{renderItem(item, index)}</React.Fragment>;
					})}
				</div>
			</SortableContext>
		</DndContext>
	);
}

SortableList.Item = SortableItem;
SortableList.DragHandle = DragHandle;
